import React, { useState, useEffect } from 'react';
import PageLoader from '../components/PageLoader';
import { api } from '../App';
import { getSpeltakImage } from '../utils/speltakImages';
import '../styles/AdminStatistiekenPage.css';

const MEDALS = ['🥇', '🥈', '🥉', '4️⃣', '5️⃣'];

function RankBar({ label, value, max, suffix = '', accent = 'blue' }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;

  return (
    <div className="stat-rank-row">
      <div className="stat-rank-label">
        <span className="stat-rank-name">{label}</span>
        {suffix && <span className="stat-rank-suffix">{suffix}</span>}
      </div>
      <div className="stat-rank-bar-wrap">
        <div
          className={`stat-rank-bar stat-rank-bar--${accent}`}
          style={{ width: `${Math.max(pct, 4)}%` }}
        />
      </div>
      <span className="stat-rank-value">{value}</span>
    </div>
  );
}

function AdminStatistiekenPage({ onBack, onLogout }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    const loadStats = async () => {
      try {
        const response = await api.get('/index.php?action=adminstatistieken');
        if (cancelled) return;
        setStats(response.data);
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        setError(err.response?.data?.message || 'Fout bij laden van statistieken');
        setLoading(false);
      }
    };

    loadStats();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return <PageLoader />;
  }

  const kpis = stats?.kpis || {};
  const topMax = stats?.top_producten?.[0]?.totaal || 1;
  const speltakMax = stats?.speltakken?.[0]?.totaal_producten || 1;
  const leverdagMax = stats?.drukste_leverdagen?.[0]?.totaal_producten || 1;
  const actieveSpeltakken = (stats?.speltakken || []).filter((s) => s.totaal_producten > 0);

  return (
    <div className="admin-statistieken-container">
      <header className="admin-header">
        <button type="button" onClick={onBack} className="back-button">← Terug</button>
        <h1>Statistieken & weetjes</h1>
        <button type="button" onClick={onLogout} className="logout-button">Uitloggen</button>
      </header>

      <main className="admin-main">
        <div className="statistieken-wrapper">
          {error && <p className="error-message">{error}</p>}

          {stats && (
            <>
              {stats.weetje && (
                <div className="stat-weetje-banner">
                  <span className="stat-weetje-icon">💡</span>
                  <p>{stats.weetje}</p>
                </div>
              )}

              <div className="stat-kpi-grid">
                <div className="stat-kpi-card stat-kpi-card--primary">
                  <span className="stat-kpi-icon">🛒</span>
                  <span className="stat-kpi-value">{kpis.totaal_producten}</span>
                  <span className="stat-kpi-label">Producten besteld</span>
                </div>
                <div className="stat-kpi-card">
                  <span className="stat-kpi-icon">📋</span>
                  <span className="stat-kpi-value">{kpis.totaal_bestellingen}</span>
                  <span className="stat-kpi-label">Bestellingen</span>
                </div>
                <div className="stat-kpi-card">
                  <span className="stat-kpi-icon">📊</span>
                  <span className="stat-kpi-value">{kpis.gemiddeld_per_bestelling}</span>
                  <span className="stat-kpi-label">Gem. per bestelling</span>
                </div>
                <div className="stat-kpi-card">
                  <span className="stat-kpi-icon">🏕️</span>
                  <span className="stat-kpi-value">{kpis.actieve_speltakken}</span>
                  <span className="stat-kpi-label">Actieve speltakken</span>
                </div>
                <div className="stat-kpi-card">
                  <span className="stat-kpi-icon">✏️</span>
                  <span className="stat-kpi-value">{kpis.totaal_notities}</span>
                  <span className="stat-kpi-label">Notities geplaatst</span>
                </div>
                <div className="stat-kpi-card">
                  <span className="stat-kpi-icon">🆓</span>
                  <span className="stat-kpi-value">{kpis.totaal_vrij}</span>
                  <span className="stat-kpi-label">Vrije producten</span>
                </div>
              </div>

              <div className="stat-cards-grid">
                <section className="stat-card stat-card--highlight">
                  <h2>🏆 Top 5 meest besteld</h2>
                  {stats.top_producten?.length > 0 ? (
                    <ul className="stat-medal-list">
                      {stats.top_producten.map((item, index) => (
                        <li key={`top-${item.product}`} className="stat-medal-item">
                          <span className="stat-medal">{MEDALS[index] || `${index + 1}.`}</span>
                          <div className="stat-medal-body">
                            <strong>{item.product}</strong>
                            {item.eenheid && <span className="stat-eenheid">({item.eenheid})</span>}
                            <RankBar
                              label=""
                              value={item.totaal}
                              max={topMax}
                              accent="gold"
                            />
                          </div>
                          <span className="stat-medal-totaal">{item.totaal}×</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="stat-empty">Nog geen data</p>
                  )}
                </section>

                <section className="stat-card">
                  <h2>🐢 Top 3 minst besteld</h2>
                  <p className="stat-card-hint">Alleen producten die minstens één keer besteld zijn</p>
                  {stats.minst_producten?.length > 0 ? (
                    <ul className="stat-simple-list">
                      {stats.minst_producten.map((item, index) => (
                        <li key={`min-${item.product}`}>
                          <span className="stat-list-rank">{index + 1}.</span>
                          <span className="stat-list-name">
                            {item.product}
                            {item.eenheid && <small> ({item.eenheid})</small>}
                          </span>
                          <span className="stat-list-value">{item.totaal}×</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="stat-empty">Nog geen data</p>
                  )}
                </section>

                <section className="stat-card stat-card--wide">
                  <h2>🏕️ Speltak-ranglijst</h2>
                  <p className="stat-card-hint">Totaal aantal producten per speltak (catalogus + vrij)</p>
                  {actieveSpeltakken.length > 0 ? (
                    <div className="stat-speltak-grid">
                      {stats.speltakken.map((speltak) => {
                        const image = getSpeltakImage(speltak.naam);
                        if (speltak.totaal_producten === 0) return null;

                        return (
                          <div key={speltak.naam} className="stat-speltak-item">
                            {image && <img src={image} alt="" className="stat-speltak-logo" />}
                            <div className="stat-speltak-info">
                              <strong className="stat-speltak-naam">{speltak.naam}</strong>
                              <RankBar
                                label=""
                                value={speltak.totaal_producten}
                                max={speltakMax}
                                accent="green"
                              />
                              <span className="stat-speltak-meta">
                                {speltak.totaal_producten} producten · {speltak.aantal_bestellingen} bestellingen
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="stat-empty">Nog geen bestellingen per speltak</p>
                  )}
                </section>

                <section className="stat-card">
                  <h2>📅 Drukste leverdagen</h2>
                  <p className="stat-card-hint">Op basis van aantal producten, niet aantal bestellingen</p>
                  {stats.drukste_leverdagen?.length > 0 ? (
                    <div className="stat-rank-list">
                      {stats.drukste_leverdagen.map((dag, index) => (
                        <RankBar
                          key={`lev-${dag.datum}`}
                          label={`${index === 0 ? '👑 ' : ''}${dag.datum}`}
                          suffix={`${dag.aantal_bestellingen} best.`}
                          value={dag.totaal_producten}
                          max={leverdagMax}
                          accent="purple"
                        />
                      ))}
                    </div>
                  ) : (
                    <p className="stat-empty">Nog geen data</p>
                  )}
                </section>

                <section className="stat-card">
                  <h2>📝 Drukste besteldagen</h2>
                  <p className="stat-card-hint">Dagen waarop speltakken het meeste invoerden</p>
                  {stats.drukste_besteldagen?.length > 0 ? (
                    <div className="stat-rank-list">
                      {stats.drukste_besteldagen.map((dag, index) => (
                        <RankBar
                          key={`best-${dag.datum}`}
                          label={`${index === 0 ? '🔥 ' : ''}${dag.datum}`}
                          suffix={`${dag.aantal_bestellingen} best.`}
                          value={dag.totaal_producten}
                          max={stats.drukste_besteldagen[0].totaal_producten}
                          accent="orange"
                        />
                      ))}
                    </div>
                  ) : (
                    <p className="stat-empty">Nog geen data</p>
                  )}
                </section>

                {stats.top_groepen?.length > 0 && (
                  <section className="stat-card">
                    <h2>📦 Populairste productgroepen</h2>
                    <ul className="stat-simple-list">
                      {stats.top_groepen.map((groep, index) => (
                        <li key={groep.naam}>
                          <span className="stat-list-rank">{MEDALS[index] || `${index + 1}.`}</span>
                          <span className="stat-list-name">{groep.naam}</span>
                          <span className="stat-list-value">{groep.totaal}×</span>
                        </li>
                      ))}
                    </ul>
                  </section>
                )}
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}

export default AdminStatistiekenPage;
